// KpiScorecard — dense token-styled KPI card primitive.
// Purely presentational: no data fetching, no i18n, no business logic.
// Labels and formatted values are passed by callers.
import type { CSSProperties } from 'react';

export interface KpiScorecardDelta {
  direction: 'up' | 'down' | 'flat';
  /** Pre-formatted percentage text (e.g. "+12%" or "3.5%"). */
  percentText: string;
  /** Full accessible label for the delta indicator (e.g. "increased 12%"). */
  ariaLabel: string;
}

export interface KpiScorecardProps {
  label: string;
  /** Pre-formatted value string — callers are responsible for number formatting. */
  value: string;
  delta?: KpiScorecardDelta;
}

// INVARIANT: token names must exist in colors_and_type.css for both light and dark.
const DELTA_COLOR: Record<KpiScorecardDelta['direction'], string> = {
  up: 'var(--status-success-fg)',
  down: 'var(--status-error-fg)',
  flat: 'var(--status-neutral-fg)',
};

// Unicode arrows — aria-hidden so the ariaLabel prop is the AT text alternative.
const DELTA_GLYPH: Record<KpiScorecardDelta['direction'], string> = {
  up: '↑',
  down: '↓',
  flat: '→',
};

const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--s-2)',
  padding: 'var(--s-4)',
  background: 'var(--bg-1)',
  border: '1px solid var(--border-1)',
  borderRadius: 'var(--r-2)',
  boxShadow: 'var(--shadow-1)',
};

const labelStyle: CSSProperties = {
  fontSize: 'var(--fz-12)',
  color: 'var(--fg-2)',
  lineHeight: 'var(--lh-ui)',
};

const valueStyle: CSSProperties = {
  fontSize: 'var(--fz-22)',
  fontWeight: 600,
  color: 'var(--fg-1)',
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 'var(--lh-tight)',
};

export function KpiScorecard({ label, value, delta }: KpiScorecardProps) {
  return (
    <div style={cardStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{value}</span>
      {delta ? (
        <span
          aria-label={delta.ariaLabel}
          data-direction={delta.direction}
          style={{
            fontSize: 'var(--fz-12)',
            color: DELTA_COLOR[delta.direction],
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--s-1)',
          }}
        >
          {/* Glyph is decorative — the aria-label on the parent is the AT alternative */}
          <span aria-hidden="true">{DELTA_GLYPH[delta.direction]}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{delta.percentText}</span>
        </span>
      ) : null}
    </div>
  );
}
